/**
 * AuditoriaApiClient -- Cliente HTTP para auditorías de Comprobante Z.
 *
 * Flujo:
 *  1. Extracción: envía archivo binary (FormData) al webhook N8N → Gemini Vision OCR
 *  2. Guardado:   persiste los datos validados en NexoAPI
 *
 * El webhook N8N espera: multipart/form-data con archivo en campo "data"
 * Query params: business_id, business_name
 *
 * @module infrastructure/api/AuditoriaApiClient
 */

import { AppConfig } from '../../config.js';
import { ApiBase } from './ApiBase.js';
import { ok, fail } from '../../domain/value-objects/CrudResult.js';

const N8N_WEBHOOK_PATH = '/webhook/auditoria-z';

// ── Adapter conciliacion banco: BankMatchResult (backend camelCase) -> match-review ──
// Backend BankMatchRow: { bankMovementId, fecha, valor(signo), tipo, descripcion, estado
//   (matched|ambiguo|sin-match|categorizado|requiere-revision), categoria, requiereRevision,
//   categoriaSugerida, match:BankMatchRef|null, candidatos:[BankMatchRef] }.
// BankMatchRef: { tipo(venta_pago|egreso), id, numeroFactura, concepto, monto, fecha }.
// match-review consume: { item_id, factura_descripcion, accion(MATCH|CREATE|REVIEW), confianza,
//   razon, insumo_id_elegido, candidatos:[{insumo_id,nombre,presentacion,unidad,score}], create_sugerido }.
function _cbMoney(n) {
  const v = Number(n);
  return isFinite(v) ? '$' + Math.abs(v).toLocaleString('es-CO', { maximumFractionDigits: 0 }) : '$0';
}
function _cbRefToCandidato(ref, score) {
  return {
    insumo_id: ref.id,
    nombre: ref.concepto || (ref.numeroFactura ? `Factura ${ref.numeroFactura}` : (ref.tipo || 'registro')),
    presentacion: ref.fecha || '',
    unidad: _cbMoney(ref.monto),
    score: typeof score === 'number' ? score : null
  };
}
export function adaptBankMatchToReview(d) {
  const rows = Array.isArray(d?.tabla) ? d.tabla : [];
  const tabla = rows.map(r => {
    const estado = String(r.estado || '').toLowerCase();
    const esDebito = String(r.tipo || '').toLowerCase() === 'debito' || Number(r.valor) < 0;
    let accion = 'REVIEW', confianza = 0.5, insumo_id_elegido = null, candidatos = [], create_sugerido = null;
    if (estado === 'matched' && r.match) {
      accion = 'MATCH'; confianza = 0.95;
      insumo_id_elegido = r.match.id;
      candidatos = [_cbRefToCandidato(r.match, 0.95)];
    } else if (estado === 'ambiguo') {
      accion = 'MATCH'; confianza = 0.6;
      candidatos = (r.candidatos || []).map(c => _cbRefToCandidato(c, 0.6));
    } else if (estado === 'sin-match' && esDebito) {
      accion = 'CREATE'; confianza = 0.4;
      create_sugerido = { nombre: r.descripcion || 'Egreso banco', presentacion: r.fecha || '', unidad: 'COP' };
    } else {
      // requiere-revision | sin-match credito | categorizado -> REVIEW
      accion = 'REVIEW';
      confianza = estado === 'categorizado' ? 0.7 : 0.4;
      candidatos = (r.candidatos || []).map(c => _cbRefToCandidato(c, 0.5));
    }
    const signo = Number(r.valor) < 0 ? '-' : '+';
    const desc = r.descripcion || `${r.tipo || 'movimiento'} ${_cbMoney(r.valor)}`;
    const razon = `${String(r.tipo || '').toUpperCase()} ${signo}${_cbMoney(r.valor)} (${r.fecha || 's/f'}) — ${r.estado || ''}` +
                  `${r.categoria ? ', ' + r.categoria : ''}${r.requiereRevision ? ' [requiere revision]' : ''}`;
    return {
      item_id: r.bankMovementId,
      factura_descripcion: desc, descripcion: desc,
      monto: Number(r.valor), fecha: r.fecha, tipo: r.tipo,
      // #2495 frente1: preservar clasificacion del backend para la bandeja de revision.
      categoria: r.categoria || null, estado: r.estado || null, requiereRevision: r.requiereRevision === true,
      // CB-F7-UI: estado ESPECIAL persistido (Frente B #107) para el switch de la bandeja.
      es_especial: r.es_especial === true || r.esEspecial === true,
      accion, confianza, razon, insumo_id_elegido, candidatos, create_sugerido
    };
  });
  const rs = d?.resumen || {};
  const resumen = {
    items_total: rs.totalMovimientos ?? tabla.length,
    match_sugeridos: rs.matched ?? tabla.filter(t => t.accion === 'MATCH').length,
    crear_sugeridos: tabla.filter(t => t.accion === 'CREATE').length,
    review: (rs.requiereRevision ?? 0) + (rs.sinMatch ?? 0),
    ambiguo: rs.ambiguo ?? 0,
    reportadas_no_llegaron: rs.reportadasNoLlegaron?.count ?? (Array.isArray(d?.reportadasNoLlegaron) ? d.reportadasNoLlegaron.length : 0)
  };
  return { tabla, resumen };
}

export const AuditoriaApiClient = {

  /**
   * Envía archivo binary al webhook N8N para extracción OCR (patrón Purchase).
   * @param {Object} params
   * @param {string} params.businessId
   * @param {string} params.businessName
   * @param {File}   params.file - Objeto File del input/drop
   */
  async extract({ businessId, businessName, file }) {
    const qs = new URLSearchParams({
      business_id: businessId,
      business_name: businessName,
    });
    const url = `${AppConfig.n8nBase}${N8N_WEBHOOK_PATH}?${qs}`;
    const formData = new FormData();
    formData.append('data', file);

    try {
      const res = await ApiBase.fetchQueCambia(url, { method: 'POST', body: formData });
      const response = await res.json().catch(() => null);
      if (!res.ok) return fail(res.status, response?.message || `Error N8N ${res.status}`, response);

      // Workflow V2.3 devuelve: { success, tipo_documento, confianza_deteccion, data, business_id }
      // Workflow antiguo devolvia: { success, data }
      return ok({
        tipo: response?.tipo_documento || 'cierre_z',
        confianza: response?.confianza_deteccion || 0,
        data: response?.data ?? response
      });
    } catch (err) {
      return fail('NETWORK', err.message || 'Error de red al contactar N8N');
    }
  },

  async list(businessId, page = 1, pageSize = 20) {
    return ApiBase.get(`/api/v1/auditoria?businessId=${businessId}&page=${page}&pageSize=${pageSize}`);
  },

  async getById(id) {
    return ApiBase.get(`/api/v1/auditoria/${id}`);
  },

  async save(businessId, data, force = false) {
    // fetch directo para poder agregar X-Force-Save header cuando se detecta duplicado
    const headers = { 'Content-Type': 'application/json' };
    if (AppConfig.token) headers['Authorization'] = `Bearer ${AppConfig.token}`;
    if (force) headers['X-Force-Save'] = '1';
    try {
      const res = await ApiBase.fetchQueCambia(`${AppConfig.apiBase}/api/v1/auditoria`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ businessId, ...data })
      });
      const payload = await res.json().catch(() => null);
      if (res.status === 409) {
        return fail(409, payload?.message || 'Duplicado detectado', payload);
      }
      if (!res.ok) {
        const msg = payload?.message || payload?.error || `Error ${res.status}`;
        return fail(res.status, msg, payload);
      }
      return ok(payload);
    } catch (err) {
      return fail('NETWORK', err.message || 'Error de red');
    }
  },

  async delete(id) {
    return ApiBase.del(`/api/v1/auditoria/${id}`);
  },

  async cleanupIncompletos(businessId) {
    return ApiBase.del(`/api/v1/auditoria/cleanup-incompletos?businessId=${businessId}`);
  },

  async checkDuplicado(businessId, fechaCierre, caja, numeroCierre) {
    const qs = new URLSearchParams({
      businessId,
      fechaCierre: fechaCierre || '',
      caja: caja || '',
      numeroCierre: String(numeroCierre || '')
    });
    return ApiBase.get(`/api/v1/auditoria/check-duplicado?${qs}`);
  },

  async metricas(businessId) {
    return ApiBase.get(`/api/v1/auditoria/metricas?businessId=${businessId}`);
  },

  async balance(businessId, desde, hasta) {
    let url = `/api/v1/auditoria/balance?businessId=${businessId}`;
    if (desde) url += `&desde=${desde}`;
    if (hasta) url += `&hasta=${hasta}`;
    return ApiBase.get(url);
  },

  async movimientos(businessId, fecha) {
    let url = `/api/v1/auditoria/movimientos?businessId=${businessId}`;
    if (fecha) url += `&fecha=${fecha}`;
    return ApiBase.get(url);
  },

  async saveConciliacion(data) {
    return ApiBase.post('/api/v1/auditoria/conciliaciones', data);
  },

  async conciliaciones(businessId, fecha) {
    let url = `/api/v1/auditoria/conciliaciones?businessId=${businessId}`;
    if (fecha) url += `&fecha=${fecha}`;
    return ApiBase.get(url);
  },

  // ============== CONCILIACION BANCARIA (bank_movements) — CB-T4 real ==============
  // Modalidad a nivel de CUENTA bancaria (nexo_business.bank_movements), distinta de la
  // conciliacion externa (datafono). Backend AuditoriaController bank-movements/* (CB-T1/T2/T3).

  /**
   * POST /api/v1/auditoria/bank-movements/upload — sube el extracto .xlsx (parser ClosedXML).
   * @param {{businessId:string, banco?:string, file:File}} params
   * @returns {Promise<{ok:boolean, data?:{businessId,banco,sourceFile,totalRows,inserted,duplicates,netByDate:Array}, error?:string}>}
   */
  async uploadBankMovements({ businessId, banco, file } = {}) {
    if (!businessId || !file) return fail('PARAMS', 'businessId y file son requeridos');
    const headers = {};
    if (AppConfig.token) headers['Authorization'] = `Bearer ${AppConfig.token}`;
    const fd = new FormData();
    fd.append('businessId', businessId);
    fd.append('file', file);
    if (banco) fd.append('banco', banco);
    try {
      const res = await ApiBase.fetchQueCambia(`${AppConfig.apiBase}/api/v1/auditoria/bank-movements/upload`, {
        method: 'POST', headers, body: fd
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) return fail(res.status, payload?.message || payload?.error || `Error ${res.status}`, payload);
      return ok(payload?.data ?? payload);
    } catch (err) {
      return fail('NETWORK', err.message || 'Error de red al subir el extracto');
    }
  },

  /**
   * GET /api/v1/auditoria/bank-movements — historial de movimientos de la cuenta.
   * @param {string} businessId
   * @param {{fecha?:string, status?:string}} opts
   * @returns {Promise<{ok:boolean, items?:Array, error?:string}>}
   */
  async listBankMovements(businessId, { fecha, status } = {}) {
    if (!businessId) return { ok: false, error: 'businessId requerido' };
    const qs = new URLSearchParams({ businessId });
    if (fecha) qs.set('fecha', fecha);
    if (status) qs.set('status', status);
    const res = await ApiBase.get(`/api/v1/auditoria/bank-movements?${qs}`);
    if (!res.ok) return { ok: false, error: res.error || 'No se pudo cargar el historial' };
    const items = res.data?.data ?? res.data ?? [];
    return { ok: true, items: Array.isArray(items) ? items : [] };
  },

  /**
   * GET /api/v1/auditoria/bank-movements/match — conciliacion banco<->sistema (READ-ONLY, CB-T2).
   * Devuelve el payload YA adaptado al contrato de match-review.
   * @param {string} businessId
   * @param {{desde:string, hasta:string}} range - yyyy-MM-dd (requeridos)
   * @returns {Promise<{ok:boolean, data?:{tabla:Array, resumen:object}, error?:string}>}
   */
  async matchBankMovements(businessId, { desde, hasta } = {}) {
    if (!businessId || !desde || !hasta) return { ok: false, error: 'businessId, desde y hasta son requeridos' };
    const qs = new URLSearchParams({ businessId, desde, hasta });
    const res = await ApiBase.get(`/api/v1/auditoria/bank-movements/match?${qs}`);
    if (!res.ok) return { ok: false, error: res.error || 'No se pudo conciliar' };
    const d = res.data?.data ?? res.data ?? {};
    return { ok: true, data: adaptBankMatchToReview(d) };
  },

  /**
   * GET /api/v1/auditoria/bank-movements/rolling — saldo rodante banco vs sistema (CB-T3).
   * @param {string} businessId
   * @param {{base:number, desde:string, hasta:string}} opts
   */
  async rollingBankBalance(businessId, { base, desde, hasta } = {}) {
    if (!businessId || base == null || !desde || !hasta) return { ok: false, error: 'businessId, base, desde y hasta requeridos' };
    const qs = new URLSearchParams({ businessId, base: String(base), desde, hasta });
    const res = await ApiBase.get(`/api/v1/auditoria/bank-movements/rolling?${qs}`);
    if (!res.ok) return { ok: false, error: res.error || 'No se pudo calcular el saldo rodante' };
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * POST /api/v1/auditoria/bank-movements/status — persiste el estado de N movimientos.
   * #2495 frente1: resolver la bandeja de revision. status: matched | ignored | pending.
   * @param {string} businessId
   * @param {Array<{bankMovementId:string, status:string}>} movimientos
   */
  async confirmBankStatus(businessId, movimientos) {
    if (!businessId || !Array.isArray(movimientos) || !movimientos.length) {
      return { ok: false, error: 'businessId y movimientos[] requeridos' };
    }
    const res = await ApiBase.post('/api/v1/auditoria/bank-movements/status', { businessId, movimientos });
    if (!res.ok) return { ok: false, error: res.error || 'No se pudo actualizar el estado' };
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * PATCH /api/v1/auditoria/bank-movements/{id}?businessId= — marca un movimiento como
   * ESPECIAL (solo panel) y/o actualiza notes/status. Contrato Frente B (Argy-A #107):
   * COALESCE (envia solo lo que cambia), devuelve el registro actualizado. es_especial=true
   * NO baja a Desktop (garantizado por guard-trigger backend, CB-F6).
   * @param {string} businessId
   * @param {string} bankMovementId
   * @param {{esEspecial?:boolean, notes?:string, status?:string}} cambios
   */
  async patchBankMovement(businessId, bankMovementId, cambios = {}) {
    if (!businessId || !bankMovementId) return { ok: false, error: 'businessId + bankMovementId requeridos' };
    const qs = new URLSearchParams({ businessId }).toString();
    const res = await ApiBase.patch(`/api/v1/auditoria/bank-movements/${encodeURIComponent(bankMovementId)}?${qs}`, cambios);
    if (!res.ok) return { ok: false, error: res.error || 'No se pudo actualizar el movimiento' };
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * BAN-T2 (D-64, nexo-api 394): PATCH /api/v1/auditoria/egresos/{id}/marca?businessId= con {marcaPrivada}
   * (null | 'gerencial' | 'personal'). Solo el dueno: 403 para los demas. El code viaja para decir la causa.
   */
  async marcarEgreso(businessId, egresoId, cuerpo) {
    if (!businessId || !egresoId) return { ok: false, error: 'businessId + egresoId requeridos' };
    const qs = new URLSearchParams({ businessId }).toString();
    const res = await ApiBase.patch(`/api/v1/auditoria/egresos/${encodeURIComponent(egresoId)}/marca?${qs}`, cuerpo);
    if (!res.ok) return { ok: false, code: res.code, error: res.error || 'No se pudo guardar la marca' };
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * POST /api/v1/auditoria/conciliaciones/egresos-confirm — crea egresos desde movimientos
   * bancarios (idempotente). #2495 frente1: reclasificar un movimiento REVIEW como egreso.
   * @param {string} businessId
   * @param {Array<{bankMovementId:string, monto:number, fecha?:string, concepto?:string, metodo?:string}>} egresos
   */
  async createEgresosFromConciliacion(businessId, egresos) {
    if (!businessId || !Array.isArray(egresos) || !egresos.length) {
      return { ok: false, error: 'businessId y egresos[] requeridos' };
    }
    const res = await ApiBase.post('/api/v1/auditoria/conciliaciones/egresos-confirm', { businessId, egresos });
    if (!res.ok) return { ok: false, error: res.error || 'No se pudieron crear los egresos' };
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },
};
